"use client";
import { Configure } from "react-instantsearch";
import AbandonedCartSuggestions from "@/components/AbandonedCartSuggestions";
import { ReadonlyURLSearchParams } from "next/navigation";
import SearchPagination from "@/components/Search/components/SearchPagination";
import SearchActiveRefinement from "@/components/Search/components/SearchActiveRefinement";
import { SearchProps } from "@/components/Search/types";
import {
  DEFAULT_SEARCH_CONFIG,
  RESULT_PRODUCT_TYPE_MAP,
  QUERY_URL_PARAM,
} from "@/components/Search/constants";
import getResultTypeFromUrl from "@/components/Search/utils/getResultTypeFromUrl";
import {
  useIsResultTabView,
  useSearchIndex,
  useProductCreatedRange,
} from "@/components/Search/components/SearchContextProvider";
import getFilters from "@/components/Search/utils/getFilters";
import { shouldExcludeOnlyArchivedCourseRuns } from "@/components/Search/utils/getRefinementListFromSearchParams";
import { mapSearchParamsToDcs } from "@/components/Search/utils/mapSearchParamsToDcs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import getProductCreatedFilters from "@/components/Search/utils/getProductCreatedFilters";
import { createSearchQueryId } from "@/utils/getSearchQueryId";
import dynamic from "next/dynamic";
import DynamicSearchFilters from "../SearchFilters/DynamicSearchFilters";
import DynamicResultsHeading from "../DynamicResultsHeading";
import useUrlSearchString from "@/components/Search/hooks/useUrlSearchString";
import useSyncSearchPage from "@/components/Search/hooks/useSyncSearchPage";
// Lazy: this module carries the supersonic-ui stylesheet (~460 KiB), and the
// `search` shortcode pulls this file into the graph of every CMS page.
const DynamicFlowWrapper = dynamic(
  () => import("@/components/DynamicFlowWrapper"),
  { ssr: false },
);

function DynamicSearchContent({
  searchConfig = {},
  queryString = "",
  forcedFlowId,
  resultsHeadingVariant,
}: SearchProps) {
  const liveSearchString = useUrlSearchString(queryString);
  useSyncSearchPage();
  const { searchIndex } = useSearchIndex();
  const { setIsResultTabView } = useIsResultTabView();
  const { productCreatedRange } = useProductCreatedRange();

  // `searchParams` reflects the live URL on the client and the server-passed
  // `queryString` on first paint. Memoized so the `URLSearchParams` instance
  // is stable across renders that don't actually change the URL — important
  // for downstream `forEach` / `get` callers and for `useEffect` deps.
  const searchParams = useMemo(
    () =>
      new URLSearchParams(
        liveSearchString,
      ) as unknown as ReadonlyURLSearchParams,
    [liveSearchString],
  );
  const pageTab = RESULT_PRODUCT_TYPE_MAP[getResultTypeFromUrl(searchParams)];
  const query = searchParams.get(QUERY_URL_PARAM);
  const productCreatedFilters = getProductCreatedFilters(productCreatedRange);
  const configForSearch = {
    ...DEFAULT_SEARCH_CONFIG,
    filters: getFilters({
      indexName: searchIndex,
      excludeOnlyArchivedCourseRuns: shouldExcludeOnlyArchivedCourseRuns(
        searchParams,
        searchIndex,
      ),
    }),
    ...searchConfig,
    ...(productCreatedFilters.length > 0 && {
      numericFilters: productCreatedFilters,
    }),
  };

  // These search params are used to pass to the DynamicFlowWrapper component.
  // Memoized on the URL string so the prop has a stable identity when the URL
  // hasn't changed — `DynamicFlowWrapper`'s `searchParamsOptions` derivation
  // is sensitive to this and would otherwise refetch on every parent render.
  const transformedSearchParamsString = useMemo(
    () => mapSearchParamsToDcs(searchParams).toString(),
    [searchParams],
  );

  if (pageTab) {
    configForSearch.facetsRefinements = { product: [pageTab] };
  }
  if (query) {
    configForSearch.query = query;
  }
  const prevQueryRef = useRef(query);
  useEffect(() => {
    if (query !== prevQueryRef.current) {
      prevQueryRef.current = query;
      createSearchQueryId();
    }
  }, [query]);

  const [resultsCount, setResultsCount] = useState<number | undefined>(
    undefined,
  );
  const handleResultsCountChange = useCallback((count: number) => {
    setResultsCount(count);
  }, []);

  useEffect(() => {
    if (pageTab) {
      setIsResultTabView(true);
    }
  }, [pageTab]);

  return (
    <>
      <Configure {...configForSearch} />
      <div className="px-4 md:px-6 bg-putty-100">
        <div className="flex flex-col gap-3 container lg:max-w-[1350px] [&&_ul]:pr-0 [&&_ul]:pl-4">
          <DynamicResultsHeading
            className="mt-0 pt-8 md:pt-14 md:pl-0"
            offeringsSearchParams={transformedSearchParamsString}
            resultsCount={resultsCount}
            resultsHeadingVariant={resultsHeadingVariant}
          />

          {/* Horizontal Filter — InstantSearch refinements follow URL via SyncInstantSearchRefinementsFromUrl */}
          <DynamicSearchFilters searchParams={searchParams} />

          <div className="flex justify-start">
            {(pageTab || productCreatedRange) && <SearchActiveRefinement />}
          </div>

          {/* Pagination */}
          <div className="flex justify-end">
            {pageTab ? <SearchPagination searchParams={searchParams} /> : null}
          </div>

          <AbandonedCartSuggestions />

          <DynamicFlowWrapper
            placement=""
            forcedFlowId={forcedFlowId}
            styleVariant="alt"
            searchParams={transformedSearchParamsString}
            onResultsCountChange={handleResultsCountChange}
          />
        </div>
      </div>
    </>
  );
}

export default DynamicSearchContent;
